import { LocalizedLink } from "@/components/LocalizedLink";
import { Phone, Mail, MapPin, Facebook, Youtube, Instagram } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import logoFooter from "@/assets/logo-footer.png";

const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
          {/* About Section */}
          <div className="lg:col-span-2">
            <img 
              src={logoFooter} 
              alt="Daniel Albert Law Firm" 
              className="h-20 w-auto mb-4"
              width="300"
              height="300"
            />
            <p className="text-sm leading-relaxed opacity-90 mb-4">
              {t('footer.description')}
            </p>
            <div className="flex gap-3">
              <a
                href="https://www.facebook.com/danielalbertlaw"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-accent transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="https://www.instagram.com/danielalbertlaw/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-accent transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href="https://www.youtube.com/@danielalbertlaw"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-accent transition-colors"
                aria-label="YouTube"
              >
                <Youtube className="h-5 w-5" />
              </a>
            </div>

            {/* Contact Info */}
            <div className="mt-8">
              <h3 className="text-lg font-bold mb-4 text-accent-light">{t('footer.contact')}</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2">
                  <MapPin className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  <span className="opacity-90">
                    10925 Beechnut St A106<br />
                    Houston, TX 77072<br />
                    United States
                  </span>
                </li>
                <li>
                  <a
                    href="tel:8329303059"
                    className="flex items-center gap-2 hover:text-accent transition-colors opacity-90 hover:opacity-100"
                  >
                    <Phone className="h-5 w-5 text-accent" />
                    <span>
                      <strong>English:</strong> (832) 930-3059
                    </span>
                  </a>
                </li>
                <li>
                  <a
                    href="tel:8323567254"
                    className="flex items-center gap-2 hover:text-accent transition-colors opacity-90 hover:opacity-100"
                  >
                    <Phone className="h-5 w-5 text-accent" />
                    <span>
                      <strong>Español:</strong> (832) 356-7254
                    </span>
                  </a>
                </li>
                <li>
                  <a
                    href="tel:8329303059"
                    className="flex items-center gap-2 hover:text-accent transition-colors opacity-90 hover:opacity-100"
                  >
                    <Phone className="h-5 w-5 text-accent" />
                    <span>
                      <strong>Tiếng Việt:</strong> (832) 930-3059
                    </span>
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:info@danielalbertlaw.com"
                    className="flex items-center gap-2 hover:text-accent transition-colors opacity-90 hover:opacity-100"
                  >
                    <Mail className="h-5 w-5 text-accent" />
                    <span>info@danielalbertlaw.com</span>
                  </a>
                </li>
              </ul>
              
              <div className="mt-6 pt-6 border-t border-primary-foreground/20">
                <h4 className="font-bold mb-2 text-accent-light">Office Hours</h4>
                <p className="text-sm opacity-90">
                  Monday–Friday: 9:00 AM – 6:00 PM<br />
                  Closed Saturday & Sunday
                </p>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-4 text-accent-light">{t('footer.quicklinks')}</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <LocalizedLink to="/" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">
                  {t('header.home')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/about-us" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">
                  {t('header.about')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/practice-areas" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">
                  {t('header.practices')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/blog" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">
                  {t('header.blog')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/tools" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">
                  {t('header.tools')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/contact" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">
                  {t('header.contact')}
                </LocalizedLink>
              </li>
            </ul>

            {/* Areas We Serve */}
            <div className="mt-8">
              <h3 className="text-lg font-bold mb-4 text-accent-light">{t('footer.areas')}</h3>
              <p className="text-sm opacity-90 mb-3">{t('footer.areas.serving')}</p>
              <ul className="space-y-1.5 text-sm">
                <li>
                  <LocalizedLink to="/areas-we-serve/cypress" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">
                    Cypress
                  </LocalizedLink>
                </li>
                <li>
                  <LocalizedLink to="/areas-we-serve/downtown" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">
                    Downtown
                  </LocalizedLink>
                </li>
                <li>
                  <LocalizedLink to="/areas-we-serve/energy-corridor" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">
                    Energy Corridor
                  </LocalizedLink>
                </li>
                <li>
                  <LocalizedLink to="/areas-we-serve/greater-southwest" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">
                    Greater Southwest
                  </LocalizedLink>
                </li>
                <li>
                  <LocalizedLink to="/areas-we-serve/heights" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">
                    Heights
                  </LocalizedLink>
                </li>
                <li>
                  <LocalizedLink to="/areas-we-serve/katy" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">
                    Katy
                  </LocalizedLink>
                </li>
                <li>
                  <LocalizedLink to="/areas-we-serve/medical-center" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">
                    Medical Center
                  </LocalizedLink>
                </li>
                <li>
                  <LocalizedLink to="/areas-we-serve/memorial" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">
                    Memorial
                  </LocalizedLink>
                </li>
                <li>
                  <LocalizedLink to="/areas-we-serve/midtown" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">
                    Midtown
                  </LocalizedLink>
                </li>
                <li>
                  <LocalizedLink to="/areas-we-serve/montrose" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">
                    Montrose
                  </LocalizedLink>
                </li>
                <li>
                  <LocalizedLink to="/areas-we-serve/river-oaks" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">
                    River Oaks
                  </LocalizedLink>
                </li>
                <li>
                  <LocalizedLink to="/areas-we-serve/spring-branch" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">
                    Spring Branch
                  </LocalizedLink>
                </li>
                <li>
                  <LocalizedLink to="/areas-we-serve/sugar-land" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">
                    Sugar Land
                  </LocalizedLink>
                </li>
                <li>
                  <LocalizedLink to="/areas-we-serve/the-woodlands" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">
                    The Woodlands
                  </LocalizedLink>
                </li>
                <li>
                  <LocalizedLink to="/areas-we-serve/uptown" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">
                    Uptown Houston
                  </LocalizedLink>
                </li>
                <li>
                  <LocalizedLink to="/areas-we-serve/west-university" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">
                    West University Place
                  </LocalizedLink>
                </li>
              </ul>
            </div>
          </div>

          {/* Services Column 1: Immigration & Criminal Defense */}
          <div>
            <h3 className="text-lg font-bold mb-4 text-accent-light">{t('practices.immigration')}</h3>
            <ul className="space-y-1.5 text-xs">
              <li><LocalizedLink to="/practice-areas/immigration-services/family-based-immigration" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Family-Based Immigration</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/immigration-services/employment-based-immigration" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Employment-Based Immigration</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/immigration-services/k-1-fiance-visa" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">K-1 Fiancé Visa</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/immigration-services/asylum-in-the-united-states" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Asylum Services</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/immigration-services/citizenship-naturalization" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Citizenship & Naturalization</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/immigration-services/deportation-removal-defense" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Deportation Defense</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/immigration-services/waivers-i601-i601a-i212" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Waivers (I-601, I-601A, I-212)</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/immigration-services/deferred-action-for-childhood-arrivals" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">DACA</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/immigration-services/vawa-petitions" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">VAWA Petitions</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/immigration-services/humanitarian-parole-tps" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Humanitarian Parole & TPS</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/immigration-services/green-card-renewal" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Green Card Renewal</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/immigration-services/consular-processing" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Consular Processing</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/immigration-services/u-visa" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">U Visa</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/immigration-services/advance-parole" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Advance Parole</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/immigration-services/re-entry-permit" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Re-entry Permit</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/immigration-services/eb-5-investor-visa" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">EB-5 Investor Visa</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/immigration-services/student-visas" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Student Visas</LocalizedLink></li>
            </ul>

            <h3 className="text-lg font-bold mb-4 mt-6 text-accent-light">{t('practices.criminal')}</h3>
            <ul className="space-y-1.5 text-xs">
              <li><LocalizedLink to="/practice-areas/criminal-defense/assault-family-violence" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Assault & Family Violence</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/criminal-defense/dwi" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">DWI</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/criminal-defense/drug-charges" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Drug Charges</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/criminal-defense/expunction-nondisclosure" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Expunction & Nondisclosure</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/criminal-defense/sex-crimes" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Sex Crimes</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/criminal-defense/theft-robbery" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Theft & Robbery</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/criminal-defense/violent-crimes" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Violent Crimes</LocalizedLink></li>
            </ul>
          </div>

          {/* Services Column 2: Family Law, Personal Injury & Tax */}
          <div>
            <h3 className="text-lg font-bold mb-4 text-accent-light">{t('practices.family')}</h3>
            <ul className="space-y-1.5 text-xs">
              <li><LocalizedLink to="/practice-areas/family-law/divorce" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Divorce</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/family-law/child-custody" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Child Custody</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/family-law/child-support" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Child Support</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/family-law/adoption" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Adoption</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/family-law/cps-defense" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">CPS Defense</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/family-law/court-order-modification" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Court Order Modification</LocalizedLink></li>
            </ul>

            <h3 className="text-lg font-bold mb-4 mt-6 text-accent-light">{t('practices.injury')}</h3>
            <ul className="space-y-1.5 text-xs">
              <li><LocalizedLink to="/practice-areas/personal-injury/auto-accident" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Auto Accident</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/personal-injury/truck-accident" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Truck Accident</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/personal-injury/medical-malpractice" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Medical Malpractice</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/personal-injury/industrial-accident" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Industrial Accident</LocalizedLink></li>
            </ul>

            <h3 className="text-lg font-bold mb-4 mt-6 text-accent-light">{t('practices.tax')}</h3>
            <ul className="space-y-1.5 text-xs">
              <li><LocalizedLink to="/practice-areas/tax-law/tax-planning" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Tax Planning</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/tax-law/tax-debt-resolution" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Tax Debt Resolution</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/tax-law/tax-litigation" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Tax Litigation</LocalizedLink></li>
            </ul>

            <h3 className="text-lg font-bold mb-4 mt-6 text-accent-light">Probate & Estate</h3>
            <ul className="space-y-1.5 text-xs">
              <li><LocalizedLink to="/practice-areas/probate-estate/estate-planning" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Estate Planning</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/probate-estate/probate-administration" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Probate Administration</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/probate-estate/guardianship" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Guardianship</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/probate-estate/special-needs-trust" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Special Needs Trust</LocalizedLink></li>
            </ul>
          </div>

          {/* Services Column 3: Business Law & Real Estate */}
          <div>
            <h3 className="text-lg font-bold mb-4 text-accent-light">{t('practices.business')}</h3>
            <ul className="space-y-1.5 text-xs">
              <li><LocalizedLink to="/practice-areas/business-law/business-formation" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Business Formation</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/business-law/contracts-agreements" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Contracts & Agreements</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/business-law/business-transactions" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Business Transactions</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/business-law/purchase-sale-business" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Purchase & Sale of Business</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/business-law/business-closing" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Business Closing</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/business-law/debt-collection" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Debt Collection</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/business-law/insurance-claims" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Insurance Claims</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/business-law/intellectual-property" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Intellectual Property</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/business-law/trademark-registration" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Trademark Registration</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/business-law/partnership-agreements" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Partnership Agreements</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/business-law/nonprofit-formation" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Non-Profit Formation</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/business-law/registered-agent" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Registered Agent Services</LocalizedLink></li>
            </ul>

            <h3 className="text-lg font-bold mb-4 mt-6 text-accent-light">Real Estate Law</h3>
            <ul className="space-y-1.5 text-xs">
              <li><LocalizedLink to="/practice-areas/real-estate-law/buying-selling" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Buying & Selling</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/real-estate-law/real-estate-closings" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Real Estate Closings</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/real-estate-law/transactions" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Transactions</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/real-estate-law/contract-review" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Contract Review</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/real-estate-law/title-services" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Title Services</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/real-estate-law/title-search" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Title Search</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/real-estate-law/landlord-tenant" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Landlord-Tenant</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/real-estate-law/lease-negotiations" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Lease Negotiations</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/real-estate-law/foreclosures-evictions" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Foreclosures & Evictions</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/real-estate-law/foreclosure-disputes" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Foreclosure Disputes</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/real-estate-law/lien-release" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Lien Release</LocalizedLink></li>
              <li><LocalizedLink to="/practice-areas/real-estate-law/affidavits-heirship" className="hover:text-accent transition-colors opacity-90 hover:opacity-100">Affidavits of Heirship</LocalizedLink></li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-primary-foreground/20 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm opacity-90">
            <p>
              © {currentYear} Daniel Albert Law Firm. {t('footer.rights')}.
            </p>
            <div className="flex gap-6">
              <LocalizedLink to="/privacy-policy" className="hover:text-accent transition-colors">
                {t('footer.legal')}
              </LocalizedLink>
              <LocalizedLink to="/terms-of-use" className="hover:text-accent transition-colors">
                {t('footer.terms')}
              </LocalizedLink>
            </div>
          </div>
          <p className="text-xs mt-4 opacity-75 text-center md:text-left">
            <strong>Disclaimer:</strong> {t('footer.disclaimer')}
          </p>
          <p className="text-xs mt-4 opacity-75 text-center md:text-left">
            Developed by{' '}
            <a 
              href="https://rocwwa.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="hover:text-accent transition-colors underline"
            >
              ROC Worldwide Agency LLC
            </a>
            {' '}/{' '}
            SEO Optimized by{' '}
            <a 
              href="https://luisroc.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="hover:text-accent transition-colors underline"
            >
              Luis ROC
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
